import { CloseButton } from '@components/utility/buttons';
import { useInviteColleagueMutation } from '@event/services/events';
import { Colleague, EmailType, EventTypes } from '@event/types/event';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ImportCSVList } from '@components/display/dialogs';
import { FormattedMessage } from 'react-intl';
import { GUEST_ACCESS_HASH_KEY } from '@event/constants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { handleError } from '@utils/common';
import { useSearchParams } from 'react-router-dom';

const InvitationSuccessfullySent = ({ plural = false }: { plural: boolean }) => (
    <div className="flex flex-col items-center max-w-[365px] text-center gap-3 m-auto">
        <div className="flex items-center justify-center size-24 rounded-full border-2">
            <CheckRoundedIcon className="size-10" />
        </div>
        <Typography fontSize={53} fontFamily="museo-sans">
            <FormattedMessage id="common.success" defaultMessage="Success" />
        </Typography>
        <Typography fontSize={26}>
            {plural ? (
                <FormattedMessage
                    id="event.bringAColleague.successMessagePlural"
                    defaultMessage="Colleagues have been invited."
                />
            ) : (
                <FormattedMessage
                    id="event.bringAColleague.successMessageSingular"
                    defaultMessage="Colleague has been invited."
                />
            )}
        </Typography>
    </div>
);

const BringColleagueDialog = (props: DialogProps) => {
    const [colleagueList, setColleagueList] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const { type, eventId } = useParams();
    const [searchParams] = useSearchParams();
    const guestAccessHash = searchParams.get(GUEST_ACCESS_HASH_KEY);
    const eventType = type as EventTypes;
    const [inviteColleagues, { isLoading: isInviteLoading }] = useInviteColleagueMutation();

    const handleClose = () => {
        props.onClose?.({}, 'escapeKeyDown');
        setColleagueList('');
        setErrorMessage('');
        setTimeout(() => {
            setShowSuccess(false);
        }, 300);
    };

    const handleInviteColleagues = useCallback(async () => {
        try {
            const colleagues: Colleague[] = colleagueList.split('\n').map((e) => ({ user: { email: e } }));

            await inviteColleagues({
                id: Number(eventId),
                type: eventType,
                colleagues,
                guestAccessHash,
            }).unwrap();

            setShowSuccess(true);
        } catch (error) {
            handleError('Error: ', error);
        }
    }, [inviteColleagues, colleagueList]);

    return (
        <Dialog
            {...props}
            disableEscapeKeyDown
            onClose={handleClose}
            slotProps={{
                paper: {
                    className: 'min-w-[828px] min-h-[500px] p-14 pb-11 rounded-2xl max-w-[626px]',
                },
            }}
        >
            <CloseButton className="absolute size-10 flex " color="black" onClick={handleClose} />
            {!showSuccess ? (
                <>
                    <DialogTitle className="mx-auto">
                        <DialogTitle align="center">
                            <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-0.8}>
                                <FormattedMessage id="event.bringAColleague.title" defaultMessage="Bring a Colleague" />
                            </Typography>
                            <Typography fontSize={16} letterSpacing={0} mt={1} color="neutral-900.main">
                                <FormattedMessage
                                    id="event.bringAColleague.description"
                                    defaultMessage="Invite attendees to joint the event via email."
                                />
                            </Typography>
                        </DialogTitle>
                    </DialogTitle>
                    <DialogContent className="py-2">
                        <ImportCSVList
                            listState={[colleagueList, setColleagueList]}
                            errorMessageState={[errorMessage, setErrorMessage]}
                            hintText={
                                <FormattedMessage
                                    id="event.bringAColleague.emailsFieldHintText"
                                    defaultMessage="List must contain the email address. Please add one email per line."
                                />
                            }
                            fieldLabel={
                                <FormattedMessage
                                    id="event.bringAColleague.emailsFieldLabel"
                                    defaultMessage="Add emails"
                                />
                            }
                            allowAdvancedMode={false}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            fullWidth
                            className="rounded-full black-border"
                            disabled={!colleagueList || !!errorMessage}
                            startIcon={isInviteLoading ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
                            onClick={handleInviteColleagues}
                        >
                            <Typography fontSize={15} fontWeight={500} letterSpacing={0.46}>
                                <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
                            </Typography>
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <InvitationSuccessfullySent plural={colleagueList.split('\n').length > 1} />
            )}
        </Dialog>
    );
};

export { BringColleagueDialog };
